/**
 * Suggested SKU for a waiting code, with one-click Accept (POST /api/sku-aliases/accept).
 *
 * <SuggestionCell row={row} country="IN" onAccepted={reload} />
 *   row: { code, suggestions: [{sku_code, product_name, confidence: high|medium|low, reason, score}], asins, same_asin }
 * Accepting saves the mapping, records the code's ASINs on the SKU (so other listings on them match by themselves)
 * and moves the waiting sales and returns in at once. Nothing is applied without a click.
 *
 * <CodeSuggestions code="BC050N-RD-RL" country="IN" onPick={setSku} />
 *   Suggestion chips for the Code Mappings dialog (debounced POST /api/sku-aliases/suggest).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { Button } from './ui/button';
import { errorMessage } from './LoadError';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/sku-aliases`;
const TONE = {
  rule: 'bg-green-100 text-green-800', high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800', low: 'bg-gray-100 text-gray-700',
};
const LABEL = { rule: 'Same code', high: 'Strong', medium: 'Check', low: 'Unlikely' };

export const ConfidenceBadge = ({ level }) => (
  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${TONE[level] || TONE.low}`}>{LABEL[level] || level}</span>
);

export const SuggestionCell = ({ row, country, onAccepted, disabled = false }) => {
  const suggestions = row.suggestions || [];
  const [choice, setChoice] = useState(suggestions[0]?.sku_code || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setChoice(suggestions[0]?.sku_code || ''); }, [row.code]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!suggestions.length) return <span className="text-[11px] text-gray-500">No similar SKU: add it to SKU Master</span>;
  const picked = suggestions.find((s) => s.sku_code === choice) || suggestions[0];
  const accept = async () => {
    if (picked.confidence === 'low' && !window.confirm(`${row.code} looks like a different model from ${picked.sku_code}. Map it anyway?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/accept`, { code: row.code, sku_code: picked.sku_code, country, asins: row.asins || [] });
      toast.success(data.message);
      onAccepted?.();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the mapping'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-w-[220px] space-y-1" data-testid={`suggestion-${row.code}`}>
      <div className="flex items-center gap-1.5">
        {suggestions.length > 1 ? (
          <select className="h-7 max-w-[170px] rounded border border-gray-300 bg-white px-1 font-mono text-[11px]" value={picked.sku_code}
            onChange={(e) => setChoice(e.target.value)} aria-label={`Suggested SKU for ${row.code}`} disabled={busy || disabled}>
            {suggestions.map((s) => <option key={s.sku_code} value={s.sku_code}>{s.sku_code} ({LABEL[s.confidence] || s.confidence})</option>)}
          </select>
        ) : <span className="font-mono text-[11px]">{picked.sku_code}</span>}
        <ConfidenceBadge level={picked.confidence} />
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={accept} disabled={busy || disabled}
          data-testid={`suggestion-accept-${row.code}`}>
          <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />{busy ? 'Saving…' : 'Accept'}
        </Button>
      </div>
      <p className="max-w-[320px] text-[10px] leading-snug text-gray-500" title={picked.product_name}>{picked.reason}</p>
      {row.same_asin?.length > 0 && (
        <p className="text-[10px] text-blue-700">Same ASIN as {row.same_asin.slice(0, 3).join(', ')}{row.same_asin.length > 3 ? ' …' : ''}: accepting maps those too.</p>
      )}
    </div>
  );
};

export const CodeSuggestions = ({ code, country, onPick }) => {
  const [result, setResult] = useState(null);
  useEffect(() => {
    const text = (code || '').trim();
    if (text.length < 3) { setResult(null); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      axios.post(`${API}/suggest`, { codes: [text], country }, { signal: controller.signal })
        .then((r) => setResult(r.data.suggestions?.[text] || null)).catch(() => {});
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [code, country]);
  if (!result) return null;
  const items = result.matched_sku
    ? [{ sku_code: result.matched_sku, confidence: 'rule', reason: result.reason }]
    : result.suggestions || [];
  if (!items.length) return <p className="text-[11px] text-gray-500">No similar SKU found.</p>;
  return (
    <div className="space-y-1" data-testid="code-suggestions">
      <p className="text-[11px] font-medium text-gray-600">Suggested</p>
      {items.map((s) => (
        <button key={s.sku_code} type="button" onClick={() => onPick(s.sku_code)}
          className="flex w-full items-start gap-2 rounded border border-gray-200 px-2 py-1 text-left hover:bg-gray-50">
          <span className="font-mono text-[12px]">{s.sku_code}</span>
          <ConfidenceBadge level={s.confidence} />
          <span className="text-[10px] text-gray-500">{s.reason}</span>
        </button>
      ))}
    </div>
  );
};
